import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';

import { ZoraType } from '@/utils/zora';

const Divider = () => (
  <div className="hidden tablet-desktop-links:block w-px h-[51px] mx-3 bg-gradient-to-b from-token-gray-300/30 via-token-gray-300/70 to-transparent" />
);

const LinkGroupSkeleton = ({ labelWidth, links }: { labelWidth: number; links: number }) => (
  <div>
    <TextSkeleton
      width={labelWidth}
      height={14}
      className="hidden tablet-desktop-links:block ml-1 mb-[7px]"
      lineClassName="mb-0"
    />
    <div className="flex">
      {Array.from({ length: links }).map((_, index) => (
        <AvatarSkeleton key={index} size={32} className="mb-1 shrink-0 mx-1" />
      ))}
    </div>
  </div>
);

const ChainAddressSkeleton = () => (
  <>
    <TextSkeleton
      width={150}
      height={18}
      className="hidden tablet:block ml-1 mt-0.5 mb-1"
      lineClassName="mb-0"
    />
    <TextSkeleton
      width={100}
      height={14}
      className="tablet:hidden ml-1 mb-1"
      lineClassName="mb-0"
    />
  </>
);

export const TokenProfileLinksSkeleton = () => (
  <div className="flex flex-row tablet:pl-0 justify-start -mx-4 px-4 tablet:mx-0 mt-3 overflow-hidden">
    <LinkGroupSkeleton labelWidth={86} links={1} />
    <Divider />
    <LinkGroupSkeleton labelWidth={58} links={1} />
    <Divider />
    <LinkGroupSkeleton labelWidth={80} links={2} />
    <Divider />
    <LinkGroupSkeleton labelWidth={48} links={2} />
  </div>
);

const ZoraPostTokenProfileHeaderLoader = () => (
  <div className="flex flex-col tablet:flex-row gap-4 tablet:gap-6 items-center p-6">
    <div className="flex-shrink-0 w-full tablet:w-auto">
      <div className="relative w-full h-auto tablet:size-45 max-h-75 tablet:max-h-45 aspect-square">
        <TextSkeleton
          width="100%"
          height="100%"
          className="size-full"
          lineClassName="mb-0 rounded-lg"
        />
        <AvatarSkeleton
          size={20}
          className="absolute -bottom-2 -right-2 border-2 border-token-gray-200"
          radius={8}
        />
      </div>
    </div>
    <div className="flex-1 min-w-0 w-full tablet:w-auto">
      <div className="flex justify-between items-start mb-4 gap-2">
        <div className="min-w-0 flex-1">
          <TextSkeleton width={250} height={34} className="ml-1" lineClassName="mb-0" />
          <TextSkeleton width={150} height={20} className="mt-2 ml-1" lineClassName="mb-0" />
        </div>
      </div>
      <ChainAddressSkeleton />
      <TokenProfileLinksSkeleton />
    </div>
  </div>
);

interface TokenProfileHeaderLoaderProps {
  zoraType?: ZoraType;
}

export const TokenProfileHeaderLoader = ({ zoraType }: TokenProfileHeaderLoaderProps) => {
  if (zoraType === 'post') {
    return <ZoraPostTokenProfileHeaderLoader />;
  }

  const isZoraCreator = zoraType === 'creator';

  return (
    <div
      className="flex flex-col p-3 pb-2 pt-4 tablet:p-5 tablet:pb-4 tablet:pt-6 bg-transparent z-10 h-fit"
      style={{ gridArea: 'profileHeader' }}
    >
      <AvatarSkeleton size={40} className="hidden tablet:inline-block ml-1 mb-2.5" />

      <div className="grid grid-flow-col justify-start gap-1 items-center mb-1 desktop:flex desktop:mb-1.5">
        <TextSkeleton
          width={250}
          height={34}
          className="hidden tablet:block ml-1"
          lineClassName="mb-0"
        />
        <TextSkeleton width={160} height={28} className="tablet:hidden ml-1" lineClassName="mb-0" />
        {!isZoraCreator && (
          <TextSkeleton width={60} height={28} className="mr-2 desktop:ml-2" lineClassName="mb-0" />
        )}
      </div>

      {isZoraCreator && (
        <TextSkeleton width={100} height={14} className="ml-1 mb-2 mt-2" lineClassName="mb-0" />
      )}
      <ChainAddressSkeleton />
      <TokenProfileLinksSkeleton />
    </div>
  );
};
